import React, { useCallback } from 'react';
import { IExtendedCustomerBPA, BpaPaymentSystem, CardBrands } from 'types';
import { cardBrands } from 'appConstants';
import Text from 'components/Text/Text';
import Button from 'components/Button/Button';
import { BpaPaymentSystemSelect } from './components/BpaPaymentSystemSelect';
import { CardBrandSelect } from './components/CardBrandSelect';
import { BankFee } from './components/BankFee';
import { KMFee } from './components/KMFee';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import style from './Tariffs.module.scss';

interface IProps {
	setId: string;
	tariff: IExtendedCustomerBPA['tariffs'][0];
	tariffs: IExtendedCustomerBPA['tariffs'];
	deleteTariff: ({ setId, tariffId }: { setId: string; tariffId: string }) => void;
	toggleIsOpenTariff: ({ setId, tariffId }: { setId: string; tariffId: string }) => void;
	addCardBrand: ({ setId, tariffId }: { setId: string; tariffId: string }) => void;
	deleteCardBrand: ({ setId, tariffId, brandId }: { setId: string; tariffId: string; brandId: string }) => void;
	changeBpaPaymentSystem: ({
		setId,
		tariffId,
		value,
	}: {
		setId: string;
		tariffId: string;
		value: BpaPaymentSystem;
	}) => void;
	changeCardBrand: ({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: CardBrands;
	}) => void;
	changeBankFee: ({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: Nullable<number>;
	}) => void;
	changeKMFee: ({
		setId,
		tariffId,
		brandId,
		value,
	}: {
		setId: string;
		tariffId: string;
		brandId: string;
		value: Nullable<number>;
	}) => void;
}

export const Tariffs: React.FC<IProps> = React.memo(
	({
		setId,
		tariff,
		tariffs,
		deleteTariff,
		toggleIsOpenTariff,
		addCardBrand,
		deleteCardBrand,
		changeBpaPaymentSystem,
		changeCardBrand,
		changeBankFee,
		changeKMFee,
	}) => {
		const expandIconClasses = classNames(style.expandIcon, { [style.rotated]: tariff.isOpenTariff });
		const expandFieldsClasses = classNames(style.row, { [style.collapseFields]: !tariff.isOpenTariff });

		const handleDeleteTariff = useCallback(() => {
			deleteTariff({ setId, tariffId: tariff.id });
		}, [deleteTariff, setId, tariff]);

		const handleToggleIsOpenTariff = useCallback(() => {
			toggleIsOpenTariff({ setId, tariffId: tariff.id });
		}, [toggleIsOpenTariff, setId, tariff]);

		const handleAddCardBrand = useCallback(() => {
			addCardBrand({ setId, tariffId: tariff.id });
		}, [addCardBrand, setId, tariff]);

		const handleDeleteCardBrand = useCallback(
			(brandId: string) => {
				deleteCardBrand({ setId, tariffId: tariff.id, brandId });
			},
			[deleteCardBrand, setId, tariff],
		);

		const handleChangeBpaPaymentSystem = useCallback(
			(value: BpaPaymentSystem) => {
				changeBpaPaymentSystem({ setId, tariffId: tariff.id, value });
			},
			[changeBpaPaymentSystem, setId, tariff],
		);

		const handleChangeCardBrand = useCallback(
			({ brandId, value }: { brandId: string; value: CardBrands }) => {
				changeCardBrand({ setId, tariffId: tariff.id, brandId, value });
			},
			[changeCardBrand, setId, tariff],
		);

		const handleChangeBankFee = useCallback(
			({ brandId, value }: { brandId: string; value: Nullable<number> }) => {
				changeBankFee({ setId, tariffId: tariff.id, brandId, value });
			},
			[changeBankFee, setId, tariff],
		);

		const handleChangeKMFee = useCallback(
			({ brandId, value }: { brandId: string; value: Nullable<number> }) => {
				changeKMFee({ setId, tariffId: tariff.id, brandId, value });
			},
			[changeKMFee, setId, tariff],
		);

		return (
			<div className={style.container}>
				<div className={style.row}>
					<BpaPaymentSystemSelect
						tariffId={tariff.id}
						tariffs={tariffs}
						onChange={handleChangeBpaPaymentSystem}
					/>
					<div className={style.flex1}>
						{tariffs.length !== 1 && (
							<div className={style.crossIconWrapper}>
								<svg className={style.crossIcon} onClick={handleDeleteTariff}>
									<use xlinkHref={`${breadcrumbs}#crossMark`} />
								</svg>
							</div>
						)}
					</div>
				</div>
				{(tariff.typeOfBpaPaymentSystem === BpaPaymentSystem.M4Bank ||
					tariff.typeOfBpaPaymentSystem === BpaPaymentSystem.Tieto) && (
					<>
						{!!tariff.data.length && (
							<>
								<Button
									className={style.expandButton}
									theme="secondaryPurple"
									disabled={tariff.data.length < 2}
									onClick={handleToggleIsOpenTariff}
									icon={
										<svg className={expandIconClasses}>
											<use xlinkHref={`${breadcrumbs}#rightAngleArrow`} />
										</svg>
									}>
									<Text
										translationNameSpace="form"
										translationKey={
											tariff.isOpenTariff ? 'hideAddedFieldsButton' : 'expandAddedFieldsButton'
										}
										asFragment={true}
									/>
								</Button>
								<div className={style.row}>
									<CardBrandSelect
										brandId={tariff.data[0].id}
										brands={tariff.data}
										onChange={handleChangeCardBrand}
									/>
									<div className={style.commissions}>
										<BankFee brandId={tariff.data[0].id} onChange={handleChangeBankFee} />
										<KMFee brandId={tariff.data[0].id} onChange={handleChangeKMFee} />
										<div className={style.controls}>
											<Button
												className={style.addButton}
												theme="primary"
												iconPosition="post"
												disabled={tariff.data.length >= cardBrands.length}
												onClick={handleAddCardBrand}
												icon={
													<svg className={style.icon}>
														<use xlinkHref={`${breadcrumbs}#plus`} />
													</svg>
												}>
												<Text
													translationNameSpace="form"
													translationKey="addButton"
													asFragment={true}
												/>
											</Button>
										</div>
									</div>
								</div>
							</>
						)}
						{tariff.data.slice(1).map((el) => (
							<div key={el.id} className={expandFieldsClasses}>
								<CardBrandSelect
									brandId={el.id}
									brands={tariff.data}
									onChange={handleChangeCardBrand}
								/>
								<div className={style.commissions}>
									<BankFee brandId={el.id} onChange={handleChangeBankFee} />
									<KMFee brandId={el.id} onChange={handleChangeKMFee} />
									<div className={style.controls}>
										<div className={style.crossIconWrapper}>
											<svg
												className={style.crossIcon}
												onClick={() => handleDeleteCardBrand(el.id)}>
												<use xlinkHref={`${breadcrumbs}#crossMark`} />
											</svg>
										</div>
									</div>
								</div>
							</div>
						))}
					</>
				)}
				{tariff.typeOfBpaPaymentSystem === BpaPaymentSystem.SBP && !!tariff.data.length && (
					<div className={style.row}>
						<BankFee brandId={tariff.data[0].id} onChange={handleChangeBankFee} />
						<KMFee brandId={tariff.data[0].id} onChange={handleChangeKMFee} />
					</div>
				)}
			</div>
		);
	},
);
